import { createSignal } from 'solid-js';
import { appearanceStorageKey, registerAppearanceLoader } from './accountAppearance';

type CelebrationPreference = 'confetti-bursts' | 'goal-star-bursts' | 'coin-jingles';

export type MoneySoundId =
  | 'coin-jingle'
  | 'cash-register'
  | 'treasure-sparkle'
  | 'piggy-plink'
  | 'celebration-bells'
  | 'drum-pop'
  | 'gentle-chime';

export const MONEY_SOUND_OPTIONS: ReadonlyArray<{
  id: MoneySoundId;
  label: string;
  description: string;
}> = [
  { id: 'coin-jingle', label: 'Coins jingling', description: 'Loose metallic coins' },
  { id: 'cash-register', label: 'Cash register', description: 'A drawer click and bright ding' },
  { id: 'treasure-sparkle', label: 'Treasure sparkle', description: 'A quick magical shimmer' },
  { id: 'piggy-plink', label: 'Piggy-bank plink', description: 'Coins dropping into a piggy bank' },
  { id: 'celebration-bells', label: 'Celebration bells', description: 'A cheerful bell flourish' },
  { id: 'drum-pop', label: 'Drum pop', description: 'A short bouncy rhythm' },
  { id: 'gentle-chime', label: 'Gentle chime', description: 'A calm, softer sound' },
];

const [confettiBurstsEnabled, setConfettiBursts] = createSignal(true);
const [goalStarBurstsEnabled, setGoalStarBursts] = createSignal(true);
const [coinJinglesEnabled, setCoinJingles] = createSignal(true);
const [moneySound, setMoneySoundSignal] = createSignal<MoneySoundId>('coin-jingle');

const preferences: ReadonlyArray<[CelebrationPreference, (value: boolean) => void]> = [
  ['confetti-bursts', setConfettiBursts],
  ['goal-star-bursts', setGoalStarBursts],
  ['coin-jingles', setCoinJingles],
];

function loadCelebrationPreferences(): void {
  for (const [name, setValue] of preferences) {
    try {
      const key = appearanceStorageKey(name);
      setValue(key === null || localStorage.getItem(key) !== 'false');
    } catch {
      setValue(true);
    }
  }
  try {
    const key = appearanceStorageKey('money-sound');
    const stored = key === null ? null : localStorage.getItem(key);
    setMoneySoundSignal(
      MONEY_SOUND_OPTIONS.some((option) => option.id === stored)
        ? (stored as MoneySoundId)
        : 'coin-jingle',
    );
  } catch {
    setMoneySoundSignal('coin-jingle');
  }
}

registerAppearanceLoader(loadCelebrationPreferences);

function setPreference(
  name: CelebrationPreference,
  setValue: (value: boolean) => void,
  value: boolean,
): void {
  setValue(value);
  try {
    const key = appearanceStorageKey(name);
    if (key !== null) localStorage.setItem(key, String(value));
  } catch {
    // Keep the preference for this session when local storage is unavailable.
  }
}

export { coinJinglesEnabled, confettiBurstsEnabled, goalStarBurstsEnabled, moneySound };

export function setConfettiBurstsEnabled(value: boolean): void {
  setPreference('confetti-bursts', setConfettiBursts, value);
}

export function setGoalStarBurstsEnabled(value: boolean): void {
  setPreference('goal-star-bursts', setGoalStarBursts, value);
}

export function setCoinJinglesEnabled(value: boolean): void {
  setPreference('coin-jingles', setCoinJingles, value);
}

export function setMoneySound(value: MoneySoundId): void {
  if (!MONEY_SOUND_OPTIONS.some((option) => option.id === value)) return;
  setMoneySoundSignal(value);
  try {
    const key = appearanceStorageKey('money-sound');
    if (key !== null) localStorage.setItem(key, value);
  } catch {
    // Keep the selected sound for this session when storage is unavailable.
  }
}
